/**
 * Report Service
 *
 * Domain layer service for report operations.
 *
 * Singleton pattern for consistent state and efficient resource usage.
 *
 * Architecture:
 * UseCase → ReportService → FirestoreService / CloudFunctionsService
 */

import { auth } from '@/data/services/firebase';
import { firestoreService } from '@/data/services/firestoreService';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { UserReportItemRemapper } from '@/domain/remappers/report/userReportItemRemapper';
import { ContentReportItemRemapper } from '@/domain/remappers/report/contentReportItemRemapper';
import type { UserReportItemEntity } from '@/domain/entities/report/userReportItemEntity';
import type { ContentReportItemEntity } from '@/domain/entities/report/contentReportItemEntity';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import type { UserReportDto } from '@/data/dtos/report/userReportDto';
import type { ContentReportDto } from '@/data/dtos/report/contentReportDto';
import type { UserReportAppealDto } from '@/data/dtos/report/userReportAppealDto';
import type { ContentReportAppealDto } from '@/data/dtos/report/contentReportAppealDto';
import type { AppNotificationDto } from '@/data/dtos/notification/appNotificationDto';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class ReportService {
  private static instance: ReportService;

  private constructor() {}

  public static getInstance(): ReportService {
    if (!ReportService.instance) {
      ReportService.instance = new ReportService();
    }
    return ReportService.instance;
  }

  /**
   * Fetch user report items by parentId
   * Matches Flutter's ReportRepository.getUserReportItems
   *
   * @param parentId - null for root items, string ID for children
   * @returns List of UserReportItemEntity ordered by 'order' field
   */
  async getUserReportItems(parentId: string | null): Promise<UserReportItemEntity[]> {
    appLogger.debug('Fetching user report items', 'ReportService.getUserReportItems', { context: { parentId } });

    const rawItems = await firestoreService.getUserReportItems(parentId);
    return UserReportItemRemapper.fromJsonList(rawItems);
  }

  /**
   * Submit a user report via cloud function
   * Matches Flutter's ReportService.reportUser + submit_report_usert_screen.dart
   *
   * @param reportItemId - ID of the selected UserReportItem
   * @param peerUserId - Firebase ID of the reported user
   * @param comment - Optional free text comment
   * @returns Promise<boolean> - Success status
   */
  async submitUserReport(
    reportItemId: string,
    peerUserId: string,
    comment: string
  ): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        appLogger.error('No authenticated user for submitting report', 'ReportService.submitUserReport');
        return false;
      }

      const now = Date.now();

      // Mirrors Flutter's UserReport object (created_timestamp uses snake_case per @JsonKey)
      const userReport: UserReportDto = {
        id: generateUuidV1(),
        userId: currentUser.uid,
        reportId: reportItemId,
        peerUserId,
        comment,
        created_timestamp: now,
      };

      // Mirrors Flutter's AppNotification + NotificationInfo + UsersInfo objects.
      // Report is anonymous: userId and name are set to "_"
      const notification: AppNotificationDto = {
        id: generateUuidV1(),
        info: {
          usersInfo: {
            userId: '_',
            name: '_',
            peerUserId,
          },
          notificationType: AppNotificationType.reportUser,
          timestamp: now,
          read: false,
          isUndo: false,
        },
      };

      await cloudFunctionsService.reportUser(userReport, notification);

      appLogger.debug('User report submitted successfully', 'ReportService.submitUserReport');
      return true;
    } catch (error) {
      appLogger.error('Error submitting user report', 'ReportService.submitUserReport', { error: error as Error });
      return false;
    }
  }

  /**
   * Fetch content report items by parentId
   * Matches Flutter's ReportRepository.getContentReportItems
   *
   * @param parentId - null for root items, string ID for children
   * @returns List of ContentReportItemEntity ordered by 'order' field
   */
  async getContentReportItems(parentId: string | null): Promise<ContentReportItemEntity[]> {
    appLogger.debug('Fetching content report items', 'ReportService.getContentReportItems', { context: { parentId } });

    const rawItems = await firestoreService.getContentReportItems(parentId);
    return ContentReportItemRemapper.fromJsonList(rawItems);
  }

  /**
   * Submit a content report via cloud function
   * Matches Flutter's ReportService.reportContent
   *
   * @param reportItemId - ID of the selected ContentReportItem
   * @param contentId - ID of the reported content
   * @param peerUserId - Firebase ID of the content owner
   * @param comment - Optional free text comment
   * @returns Promise<boolean> - Success status
   */
  async submitContentReport(
    reportItemId: string,
    contentId: string,
    peerUserId: string,
    comment: string
  ): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        appLogger.error('No authenticated user for submitting content report', 'ReportService.submitContentReport');
        return false;
      }

      const now = Date.now();

      // Mirrors Flutter's ContentReport object (created_timestamp uses snake_case per @JsonKey)
      const contentReport: ContentReportDto = {
        id: generateUuidV1(),
        userId: currentUser.uid,
        reportId: reportItemId,
        contentId,
        peerUserId,
        comment,
        created_timestamp: now,
      };

      // Report is anonymous: userId and name are set to "_"
      const notification: AppNotificationDto = {
        id: generateUuidV1(),
        info: {
          usersInfo: {
            userId: '_',
            name: '_',
            peerUserId,
          },
          notificationType: AppNotificationType.reportContent,
          timestamp: now,
          read: false,
          isUndo: false,
        },
      };

      await cloudFunctionsService.reportContent(contentReport, notification);

      appLogger.debug('Content report submitted successfully', 'ReportService.submitContentReport');
      return true;
    } catch (error) {
      appLogger.error('Error submitting content report', 'ReportService.submitContentReport', { error: error as Error });
      return false;
    }
  }

  /**
   * Submit a user report appeal via cloud function
   * Matches Flutter's ReportService.appealReportedUser + UserReportAppealScreen
   *
   * @param comment - Required appeal text (max 200 chars)
   * @returns Promise<boolean> - Success status
   */
  async appealReportedUser(comment: string): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        appLogger.error('No authenticated user for submitting appeal', 'ReportService.appealReportedUser');
        return false;
      }

      const appeal: UserReportAppealDto = {
        id: generateUuidV1(),
        userId: currentUser.uid,
        comment,
        created_timestamp: Date.now(),
      };

      await cloudFunctionsService.appealReportedUser(appeal);

      appLogger.debug('User report appeal submitted successfully', 'ReportService.appealReportedUser');
      return true;
    } catch (error) {
      appLogger.error('Error submitting user report appeal', 'ReportService.appealReportedUser', { error: error as Error });
      return false;
    }
  }

  /**
   * Submit a content report appeal via cloud function
   * Matches Flutter's ReportService.appealReportedContent + ContentReportAppealScreen
   * (lib/presentation/screens/content/content_report_appeal_screen.dart).
   *
   * @param contentId - The ID of the blocked content being appealed
   * @param comment - Required appeal text
   */
  async appealReportedContent(contentId: string, comment: string): Promise<boolean> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        appLogger.error('No authenticated user for content appeal', 'ReportService.appealReportedContent');
        return false;
      }

      const appeal: ContentReportAppealDto = {
        id: generateUuidV1(),
        userId: currentUser.uid,
        contentId,
        comment,
        created_timestamp: Date.now(),
      };

      await cloudFunctionsService.appealReportedContent(appeal);
      return true;
    } catch (error) {
      appLogger.error('Error submitting content report appeal', 'ReportService.appealReportedContent', { error: error as Error });
      return false;
    }
  }
}

export const reportService = ReportService.getInstance();
